import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ArrowLeft, ShoppingBag, Lock, Truck, Leaf, Sparkles, Info } from "lucide-react";
import { getProduct, PRODUCT_KEYS, PRODUCTS } from "@/data/packs";
import { ZoomImage } from "@/components/ZoomImage";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-[var(--oz-gold)]" />}
      <h2 className="font-serif text-xl text-[var(--oz-text)]">{title}</h2>
    </div>
    <div className="text-sm text-[var(--oz-muted)] leading-relaxed">{children}</div>
  </div>
);

export default function ProductDetailPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const product = getProduct(key);

  useSeo({
    title: product ? `${product.name} — soin cheveux Ozentya` : "Produit — Ozentya",
    description: product ? `${product.intro} ${product.tagline}. Inclus dans les packs Ozentya.` : "",
    path: `/produit/${key}`,
    image: product ? `${SITE_URL}${product.image}` : undefined,
  });

  useJsonLd(
    "ld-product",
    product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: `${SITE_URL}${product.image}`,
          description: product.description,
          brand: { "@type": "Brand", name: "Ozentya" },
          category: "Soins capillaires",
        }
      : null
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [key]);

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl mb-4">Produit introuvable</h1>
        <Link to="/" className="text-[var(--oz-gold)] font-semibold">Retour à l'accueil</Link>
      </div>
    );
  }

  const others = PRODUCT_KEYS.filter((k) => k !== key);

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-10 md:py-14">
      <button
        data-testid="product-detail-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)] mb-8"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Image avec zoom */}
        <div className="lg:sticky lg:top-24 h-fit">
          <ZoomImage
            testId="product-detail-image"
            src={product.image}
            alt={product.name}
            zoom={2.2}
            showHint
            className="rounded-[1.75rem] shadow-[0_30px_60px_-30px_rgba(26,23,20,0.5)] aspect-square bg-white"
          />
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-[var(--oz-muted)]">
            <span className="inline-flex items-center gap-1.5"><Leaf className="w-4 h-4 text-[var(--oz-gold)]" /> Actifs naturels</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-[var(--oz-gold)]" /> Marque française</span>
            <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-[var(--oz-gold)]" /> Inclus dans nos packs</span>
          </div>
        </div>

        {/* Contenu */}
        <div>
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)] mb-3">
            {product.tagline}
          </span>
          <h1 data-testid="product-detail-name" className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-[var(--oz-text)]">
            {product.name}
          </h1>
          <p className="mt-5 text-base text-[var(--oz-muted)] leading-relaxed">{product.intro}</p>

          <div className="mt-4 inline-flex items-center gap-2 bg-[var(--oz-nude)] text-[var(--oz-badge)] text-xs font-semibold px-3 py-1.5 rounded-full">
            <Sparkles className="w-3.5 h-3.5" /> Inclus dans les packs Ozentya
          </div>

          <div className="mt-8 space-y-8">
            <Section icon={Info} title="Description">
              <p>{product.description}</p>
            </Section>

            <Section icon={Check} title="Les bénéfices">
              <ul className="space-y-2.5">
                {product.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[var(--oz-nude)] text-[var(--oz-badge)] flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={Sparkles} title="Conseils d'utilisation">
              <p>{product.usage}</p>
            </Section>

            <Section icon={Leaf} title="Pour qui ?">
              <ul className="space-y-2.5">
                {product.forWho.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[var(--oz-nude)] text-[var(--oz-badge)] flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <div className="bg-[var(--oz-bg-2)] border border-[var(--oz-border)] rounded-2xl p-5">
              <h2 className="font-serif text-lg text-[var(--oz-text)] mb-2">Composition (INCI)</h2>
              <p className="text-xs text-[var(--oz-muted)] leading-relaxed">{product.ingredients}</p>
            </div>
          </div>

          <div className="mt-8">
            <Link
              to="/packs#packs"
              data-testid="product-detail-view-packs"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[var(--oz-gold)] text-white font-semibold text-sm tracking-wide hover:bg-[var(--oz-gold-hover)] transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> DÉCOUVRIR LES PACKS
            </Link>
          </div>
        </div>
      </div>

      {/* Autres produits */}
      <div className="mt-16 pt-12 border-t border-[var(--oz-border)]">
        <h2 className="font-serif text-2xl text-[var(--oz-text)] mb-6">Découvrez aussi</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {others.map((k) => {
            const p = PRODUCTS[k];
            return (
              <Link
                key={k}
                to={`/produit/${k}`}
                data-testid={`related-product-${k}`}
                className="group bg-white rounded-2xl border border-[var(--oz-border)] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <div className="aspect-square bg-[var(--oz-bg-2)] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-3 text-center">
                  <span className="text-[12px] font-semibold text-[var(--oz-text)] leading-tight">{p.name}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
